<template>
  <div class="room-facility-form">
    <b-card>
      <template #header>
        <h2 class="mb-0">{{ isEdit ? $t('room_facilities.edit_title') : $t('room_facilities.create_title') }}</h2>
      </template>
      <b-form @submit.prevent="handleSubmit" :validated="validated" novalidate>

        <b-form-group :label="$t('room_types.title')" label-for="room_type_id" :state="!hasError('room_type_id')"
          :invalid-feedback="getError('room_type_id')">
          <treeselect id="room_type_id" v-model="formFields.room_type_id" :options="roomOptions" :state="!hasError('room_type_id')"
            :class="hasError('room_type_id') ? 'error-input' : ''" placeholder="Select Room Type"
            @input="removeError('room_type_id')">
          </treeselect>
        </b-form-group>
        <b-form-group :label="$t('room_facilities.form.facility_label')" label-for="facility_id"
          :state="!hasError('facility_id')" :invalid-feedback="getError('facility_id')">

          <treeselect id="facility_id" v-model="formFields.facility_id" :options="facilityOptions"
            :normalizer="normalizer" :state="!hasError('facility_id')"
            :class="hasError('facility_id') ? 'error-input' : ''" placeholder="Select Facility"
            @input="removeError('facility_id')"></treeselect>
        </b-form-group>
        <div class="d-flex gap-2 mt-4">
          <b-button type="submit" variant="primary" :disabled="isSubmitting">
            <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
            {{ isSubmitting ? $t('common.saving') : $t('common.save') }}
          </b-button>
          <b-button variant="secondary" @click="cancel">{{ $t('common.cancel') }}</b-button>
        </div>
        <div v-if="formLevelError" class="mt-3">
          <b-alert show variant="danger" class="mb-0">
            {{ formLevelError }}
          </b-alert>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import NotifyMixin from "@/Util/NotifyMixin";
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
export default {
  name: "RoomFacilityForm",
  mixins: [NotifyMixin],
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },
  components: {
    Treeselect,
  },
  data() {
    return {
      isSubmitting: false,
      validated: false,
      formErrors: {},
      formLevelError: "",
      formFields: {
        name: "",
        is_active: 1,
        room_type_id: null,
        facility_id: null,
      },
      roomOptions: [],
      facilityOptions: [],
    };
  },
  computed: {
    isEdit() {
      return !!this.id;
    },
  },
  methods: {
    normalizer(node) {
      return {
        id: node.id,
        label: node.name
      };
    },
    hasError(field) {
      return !!this.formErrors[field];
    },
    getError(field) {
      return this.formErrors[field] ? this.formErrors[field][0] : "";
    },
    removeError(field) {
      if (this.formErrors[field]) {
        delete this.formErrors[field];
      }
      this.formLevelError = "";
    },
    async fetchRooms() {
      try {
        const response = await request({ method: 'get', url: '/room-types' });
        this.roomOptions = response.data.map((room) => ({
          id: String(room.id),
          label: `${room.hotel?.name || 'Hotel'} - ${room.name || 'Room'} (${room.bed_type || 'Bed Type'})`,
        }));
      } catch (e) {
        this.roomOptions = [];
      }
    },
    async fetchFacilities() {
      try {
        const response = await request({ method: 'get', url: '/facilities' });
        this.facilityOptions = response.data || [];
      } catch (e) {
        this.facilityOptions = [];
      }
    },
    async fetchRoomFacility() {
      try {
        const response = await request({
          method: "get",
          url: `/room-facilities/${this.id}`,
        });
        this.formFields = {
          ...this.formFields,
          ...response.data,
        };
        await this.fetchRooms();
        await this.fetchFacilities();
      } catch (error) {
        this.formLevelError = error.response?.data?.message || this.$t("room_facilities.fetch_error");
        this.notifyError(this.formLevelError);
        this.$router.push("/admin/room-facilities");
      }
    },
    async handleSubmit() {
      try {
        this.validated = true;
        this.isSubmitting = true;
        this.formErrors = {};
        this.formLevelError = "";

        if (!this.formFields.room_type_id) {
          this.formErrors.room_type_id = [this.$t("room_facilities.validation.room_required")];
        }
        if (!this.formFields.facility_id) {
          this.formErrors.facility_id = [this.$t("room_facilities.validation.facility_required")];
        }
        if (Object.keys(this.formErrors).length > 0) {
          throw new Error("Validation failed");
        }
        console.log("Submitting form with data:", this.formFields);
        
        await request({
          method: this.isEdit ? "put" : "post",
          url: this.isEdit ? `/room-facilities/${this.id}` : "/room-facilities",
          data: this.formFields,
        });
        this.notifySuccess(
          this.isEdit ? this.$t("room_facilities.update_success") : this.$t("room_facilities.create_success")
        );
        this.$router.push("/admin/room-facilities");
      } catch (error) {
        console.log("Error submitting form:", error);
        
        if (error.status === 422) {
          this.formErrors = error.data.message || {};
          this.formLevelError = this.formErrors;
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    cancel() {
      this.$router.push("/admin/room-facilities");
    },
  },
  created() {
    this.fetchRooms();
    this.fetchFacilities();
    if (this.isEdit) {
      this.fetchRoomFacility();
    }
  },
};
</script>

<style scoped>
.room-facility-form {
  padding: 20px;
}
</style>